import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Mediator } from '@spotahome/soyuz-mediator';

import { CHANNELS } from '../utils/constants';

import AvailabilityFilter from './components/AvailabilityFilter';
import BudgetFilter from './components/BudgetFilter';
import PropertyTypeFilter from './components/PropertyTypeFilter';

const noop = () => {};

const ExposedFilters = ({
  filters = {},
  cityId,
  currencyIsoCode,
  totalResults,
  onFacetFiltersUpdate = noop,
  onFiltersUpdate = noop,
  onFiltersOpen = noop,
  onFiltersClose = noop,
  onShowResultsClick = noop,
  maxPriceInCity,
  minimumStaying,
  sortingAlgorithm,
  showResults = false,
  applyAllFiltersAtOnce = false
}) => {
  const [openFilter, setOpenFilter] = useState(false);

  const [pendingFilters, setPendingFilters] = useState({});

  const applyPendingFilters = useCallback(() => {
    if (!applyAllFiltersAtOnce) return;
    setPendingFilters(current => {
      if (Object.keys(current).length > 0) {
        onFiltersUpdate(current);
      }
      return {};
    });
  }, [applyAllFiltersAtOnce, onFiltersUpdate]);

  const handleFiltersUpdate = useCallback(
    change => {
      if (applyAllFiltersAtOnce) {
        setPendingFilters(prev => ({ ...prev, ...change }));
      } else {
        onFiltersUpdate(change);
      }
      onFacetFiltersUpdate(change);
    },
    [applyAllFiltersAtOnce, onFiltersUpdate, onFacetFiltersUpdate]
  );

  const effectiveFilters = applyAllFiltersAtOnce
    ? { ...filters, ...pendingFilters }
    : filters;

  useEffect(() => {
    const handleFiltersCloseOnEvent = () => {
      setOpenFilter(false);
    };

    Mediator.subscribe(
      CHANNELS.CLOSE_EXPOSED_FILTER,
      handleFiltersCloseOnEvent
    );

    return () => {
      Mediator.unsubscribe(
        CHANNELS.CLOSE_EXPOSED_FILTER,
        handleFiltersCloseOnEvent
      );
    };
  }, []);

  const toggleExposedFilter = filterName => () => {
    const isFilterOpen = openFilter === filterName;

    if (isFilterOpen) {
      if (applyAllFiltersAtOnce) {
        applyPendingFilters();
      }
      setOpenFilter(null);
      onFiltersClose(filterName);
      Mediator.publish(CHANNELS.CLOSE_EXPOSED_FILTER);
    } else {
      setOpenFilter(filterName);
      onFiltersOpen(filterName);
      Mediator.publish(CHANNELS.OPEN_EXPOSED_FILTER);
    }
  };

  return (
    <>
      <AvailabilityFilter
        moveIn={effectiveFilters['move-in']}
        moveOut={effectiveFilters['move-out']}
        selectedRentalTypes={effectiveFilters['rentalType[]'] ?? []}
        isFilterOpen={openFilter === 'date'}
        onToggleFilter={toggleExposedFilter('date')}
        onFiltersUpdate={handleFiltersUpdate}
        minimumStaying={minimumStaying}
        totalResults={totalResults}
        moveOutFrom={effectiveFilters.moveOutFrom}
        moveOutTo={effectiveFilters.moveOutTo}
        moveInFrom={effectiveFilters.moveInFrom}
        moveInTo={effectiveFilters.moveInTo}
        showResults={showResults}
        onShowResultsClick={onShowResultsClick('date')}
      />
      <PropertyTypeFilter
        selectedPropertyTypes={effectiveFilters['type[]'] || []}
        isFilterOpen={openFilter === 'property-type'}
        onToggleFilter={toggleExposedFilter('property-type')}
        onFiltersUpdate={handleFiltersUpdate}
        totalResults={totalResults}
        filters={effectiveFilters}
        showResults={showResults}
      />
      <BudgetFilter
        isFilterOpen={openFilter === 'price'}
        onToggleFilter={toggleExposedFilter('price')}
        currencyIsoCode={currencyIsoCode}
        onFiltersUpdate={handleFiltersUpdate}
        filters={effectiveFilters}
        totalResults={totalResults}
        sortingAlgorithm={sortingAlgorithm}
        maxPriceInCity={maxPriceInCity}
        cityId={cityId}
        showResults={showResults}
      />
    </>
  );
};

ExposedFilters.propTypes = {
  totalResults: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    flexibleMoveIn: PropTypes.number,
    flexibleMoveOut: PropTypes.number,
    budget: PropTypes.string,
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    'rentalType[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'features[]': PropTypes.arrayOf(PropTypes.string),
    moveInFrom: PropTypes.string,
    moveInTo: PropTypes.string,
    moveOutFrom: PropTypes.string,
    moveOutTo: PropTypes.string
  }),
  cityId: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func,
  onFiltersOpen: PropTypes.func,
  onFiltersClose: PropTypes.func,
  onFacetFiltersUpdate: PropTypes.func,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string.isRequired,
  showResults: PropTypes.bool,
  onShowResultsClick: PropTypes.func,
  applyAllFiltersAtOnce: PropTypes.bool
};

export default ExposedFilters;
